import { SignInButton, UserButton, useUser, useOrganization } from "@clerk/clerk-react";
import { Authenticated, Unauthenticated, useQuery } from "convex/react";
import { useNavigate, useLocation } from "react-router-dom";
import { api } from "../../../../convex/_generated/api";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import { Menu, Bell, Bot, BotMessageSquare, Database } from "lucide-react";
import { NotificationsPanel } from "@/components/messaging/NotificationsPanel";
import { MessageButton } from "@/components/messaging/MessageButton";
import { useAgentSidebar } from "@/contexts/AgentSidebarContext";
import { UpgradePromptDialog } from "@/components/billing/UpgradePromptDialog";

export function Navbar() {
  const { user, isLoaded } = useUser();
  const { organization } = useOrganization();
  const { showSidebar, hideSidebar, isVisible: isAgentSidebarVisible, isMinimized } = useAgentSidebar();

  const subscription = useQuery(api.subscriptions.getUserSubscription, user ? {} : "skip");
  type UpgradeFeature = "connections" | "businessAssistant" | "documentWriter" | null;
  const [upgradeFeature, setUpgradeFeature] = useState<UpgradeFeature>(null);

  const personalWorkspace = useQuery(
    api.personalWorkspaces.getByUser,
    user ? {} : "skip"
  );
  const unreadNotificationCount = useQuery(
    api.notifications.getUnreadNotificationCount,
    user ? {} : "skip"
  );
  const navigate = useNavigate();
  const location = useLocation();
  const userData = useQuery(
    api.users.getUserByToken,
    user?.id ? { tokenIdentifier: user.id } : "skip"
  );
  const [isNotificationsPanelOpen, setIsNotificationsPanelOpen] = useState(false);

  const upgradeProps = {
    connections: {
      title: "Upgrade to Starter",
      description: "Connect third-party apps to power AI features and automate workflows. Connections are securely synced via Composio.",
      feature: "App Connections",
      planName: "Starter",
      planFeatures: ["Unlimited Bids & Proposals", "Connect apps like Gmail & Notion", "Advanced Analytics"]
    },
    businessAssistant: {
      title: "Upgrade to Starter",
      description: "Access your AI Business Assistant to help with proposals, document generation, and business insights.",
      feature: "Business Assistant",
      planName: "Starter",
      planFeatures: ["Unlimited Bids & Proposals", "Connect apps like Gmail & Notion", "Advanced Analytics"]
    },
    documentWriter: {
      title: "Upgrade to Starter",
      description: "Use the AI Document Writer to automatically generate, format, and review important business documents.",
      feature: "AI Document Writer",
      planName: "Starter",
      planFeatures: ["Unlimited Bids & Proposals", "Connect apps like Gmail & Notion", "Advanced Analytics"]
    }
  };

  const currentUpgradeProps = upgradeFeature ? upgradeProps[upgradeFeature] : ({} as any);

  return (
    <>
      <UpgradePromptDialog
        isOpen={!!upgradeFeature}
        onClose={() => setUpgradeFeature(null)}
        {...currentUpgradeProps}
      />
      <NotificationsPanel
        isOpen={isNotificationsPanelOpen}
        onClose={() => setIsNotificationsPanelOpen(false)}
      />
      <nav className="sticky top-0 z-50 w-full bg-white shadow-sm border-b border-gray-200">
        <div className="w-full px-4 md:px-6 lg:px-8">
          <div className="flex h-16 w-full items-center justify-between gap-4">
            {/* Left section: Company Name & Business Assistant */}
            <div className="flex items-center min-w-0 flex-shrink-0">
              {/* Mobile sidebar toggle button */}
              {location.pathname !== "/agent-mode" && (
                <button
                  onClick={() => document.dispatchEvent(new CustomEvent("toggle-mobile-sidebar"))}
                  className="mr-2 text-gray-600 hover:text-blue-600 focus:outline-none sm:hidden"
                >
                  <Menu className="h-6 w-6" />
                </button>
              )}

              <div className="flex items-center gap-3">
                <span className="whitespace-nowrap text-sm sm:text-base font-bold text-gray-900 truncate">
                  {organization?.name || personalWorkspace?.companyName || "ManageClean Pro"}
                </span>

                <div className="h-5 w-px bg-gray-200 hidden sm:block"></div>

                {/* Business Assistant Trigger with Avatar directly to the right of company name */}
                <button
                  id="os-nav-business-assistant"
                  onClick={() => {
                    if (location.pathname === "/agent-mode" || location.pathname === "/document-writer") {
                      navigate("/dashboard");
                      setTimeout(() => {
                        showSidebar();
                      }, 150);
                    } else {
                      if (isAgentSidebarVisible && !isMinimized) {
                        hideSidebar();
                      } else {
                        showSidebar();
                      }
                    }
                  }}
                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl bg-blue-50/90 hover:bg-blue-100/90 text-blue-700 border border-blue-200/80 text-xs font-semibold shadow-2xs transition-all hover:shadow-xs flex-shrink-0"
                  title="Open Business Assistant"
                >
                  <div className="w-5 h-5 rounded-lg bg-blue-600 text-white flex items-center justify-center flex-shrink-0 shadow-xs">
                    <Bot className="w-3.5 h-3.5" />
                  </div>
                  <span className="hidden sm:inline">Business Assistant</span>
                </button>

                {/* Document Writer Trigger in purple style directly to the right of Business Assistant */}
                <button
                  id="os-nav-document-writer"
                  onClick={() => {
                    navigate("/document-writer");
                  }}
                  className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-xl text-xs font-semibold shadow-2xs transition-all hover:shadow-xs flex-shrink-0 ${
                    location.pathname === "/document-writer"
                      ? "bg-purple-100 text-purple-800 border border-purple-300 ring-1 ring-purple-400/30"
                      : "bg-purple-50/90 hover:bg-purple-100/90 text-purple-700 border border-purple-200/80"
                  }`}
                  title="Open Document Writer"
                >
                  <div className="w-5 h-5 rounded-lg bg-purple-600 text-white flex items-center justify-center flex-shrink-0 shadow-xs">
                    <Bot className="w-3.5 h-3.5" />
                  </div>
                  <span className="hidden sm:inline">Document Writer</span>
                </button>
              </div>
            </div>

            {/* Center section: Large Segmented Toggle [ App | Agent Workforce ] - Temporarily hidden */}
            <div className="flex items-center justify-center flex-1" />

            {/* Right section: Notifications, Messages, User */}
            <div className="flex items-center justify-end gap-3 flex-shrink-0">
              {isLoaded ? (
                <div className="flex items-center gap-3">
                  <Authenticated>
                    <div className="flex items-center gap-3">
                      {/* Notifications Icon */}
                      <Button
                        variant="ghost"
                        size="icon"
                        className="relative"
                        onClick={() => setIsNotificationsPanelOpen(true)}
                        title="Notifications"
                      >
                        <Bell className="h-5 w-5 text-gray-600" />
                        {(unreadNotificationCount ?? 0) > 0 && (
                          <span className="absolute right-1 top-1 h-2 w-2 rounded-full bg-red-500"></span>
                        )}
                      </Button>
                      <MessageButton currentUserId={user?.id} />
                      {userData && (
                        <span className="hidden text-gray-900 text-sm font-medium md:inline">{userData.name}</span>
                      )}
                      <UserButton afterSignOutUrl="/" />
                    </div>
                  </Authenticated>
                  <Unauthenticated>
                    <SignInButton mode="modal" signUpFallbackRedirectUrl="/dashboard">
                      <Button className="rounded-lg bg-blue-600 px-4 py-2 font-medium text-white transition-colors duration-200 hover:bg-blue-700">
                        Sign In
                      </Button>
                    </SignInButton>
                  </Unauthenticated>
                </div>
              ) : (
                <div className="flex items-center space-x-3">
                  <div className="hidden h-6 w-24 animate-pulse rounded bg-gray-200 sm:block"></div>
                  <div className="h-8 w-8 animate-pulse rounded-full bg-gray-200"></div>
                </div>
              )}
            </div>
          </div>
        </div>
      </nav>
    </>
  );
}
